/* @jsx h */
import { defineComponent, h, onMounted, ref, watch } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { ShoppingListModal } from "../Stock/ShoppingListModal";
import { AttentionList } from "./AttentionList";
import { KpiTile } from "@app/vue/components/Dashboard/KpiTile";
import { WeeklyBars } from "@app/vue/components/Dashboard/WeeklyBars";
import { changePct, type Dashboard } from "./types";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const PERIODS = [7, 30, 90];

const money = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The first page of Inventory: what needs doing, food cost, stock value,
// purchases and waste, items running low, price rises.
// See docs/plans/inventory_and_costing/09_insight_and_ordering.md (#4).
export const InventoryOverviewPage = defineComponent({
  name: "InventoryOverviewPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const router = useRouter();

    const days = ref(30);
    const data = ref<Dashboard | null>(null);
    const loading = ref(true);
    const shopping = ref(false);

    const load = async () => {
      loading.value = true;
      try {
        data.value = (await trpc.stock.dashboard.query({
          days: days.value,
        })) as Dashboard;
      } catch {
        toast.showToast(t("inventory.overview.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);
    watch(days, load);

    const card = (title: string, body: unknown, action?: unknown) => (
      <section class="rounded-xl border border-base-300 bg-base-100 p-4">
        <div class="mb-3 flex items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-accent">{title}</h3>
          {action}
        </div>
        {body}
      </section>
    );

    const quick = (icon: string, label: string, to: string) => (
      <button
        class="btn btn-ghost btn-sm border-base-300"
        onClick={() => router.push(to)}
      >
        <span class="material-symbols-outlined text-base">{icon}</span>
        {label}
      </button>
    );

    const renderKpis = (d: Dashboard) => {
      const fc = d.food_cost.current;
      const fcPrev = d.food_cost.previous;
      const fcChange =
        fc?.cost_pct != null && fcPrev?.cost_pct
          ? fc.cost_pct - fcPrev.cost_pct
          : null;
      return (
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile
            icon="pie_chart"
            label={t("inventory.overview.food_cost")}
            value={fc?.cost_pct != null ? `${fc.cost_pct.toFixed(1)}%` : "—"}
            hint={
              fc?.costed_share != null
                ? t("inventory.overview.costed_share").replace(
                    "{pct}",
                    fc.costed_share.toFixed(0),
                  )
                : t("inventory.overview.no_costed_sales")
            }
            change={fcChange}
            riseIsBad
            to="/app/inventory/margins"
          />
          <KpiTile
            icon="inventory_2"
            label={t("inventory.overview.stock_value")}
            value={money(d.stock.value)}
            hint={t("inventory.overview.items").replace(
              "{count}",
              String(d.stock.items),
            )}
            to="/app/inventory/stock"
          />
          <KpiTile
            icon="local_shipping"
            label={t("inventory.overview.purchases")}
            value={money(d.purchases.total)}
            change={changePct(d.purchases.total, d.purchases.previous_total)}
            riseIsBad
            hint={t("inventory.overview.last_days").replace(
              "{days}",
              String(d.days),
            )}
            to="/app/inventory/deliveries"
          />
          <KpiTile
            icon="delete_sweep"
            label={t("inventory.overview.waste")}
            value={money(d.waste.total)}
            change={changePct(d.waste.total, d.waste.previous_total)}
            riseIsBad
            hint={
              d.waste.reasons[0]
                ? t(`inventory.waste.reason_${d.waste.reasons[0].reason}`)
                : t("inventory.overview.last_days").replace(
                    "{days}",
                    String(d.days),
                  )
            }
            to="/app/inventory/waste"
          />
        </div>
      );
    };

    const renderMoney = (d: Dashboard) => (
      <dl class="grid grid-cols-2 gap-3 text-sm">
        <RouterLink
          to="/app/inventory/invoices"
          class="rounded-lg bg-base-200 p-3 hover:bg-base-300/60"
        >
          <dt class="text-xs text-base-content/50">
            {t("inventory.invoices.tile_open")}
          </dt>
          <dd class="text-lg font-semibold tabular-nums">
            {money(d.invoices.open_total)}
          </dd>
        </RouterLink>
        <RouterLink
          to="/app/inventory/invoices"
          class="rounded-lg bg-base-200 p-3 hover:bg-base-300/60"
        >
          <dt class="text-xs text-base-content/50">
            {t("inventory.invoices.tile_overdue")}
          </dt>
          <dd
            class={[
              "text-lg font-semibold tabular-nums",
              d.invoices.overdue_total > 0 ? "text-error" : "",
            ].join(" ")}
          >
            {money(d.invoices.overdue_total)}
          </dd>
        </RouterLink>
        <RouterLink
          to="/app/inventory/invoices"
          class="rounded-lg bg-base-200 p-3 hover:bg-base-300/60"
        >
          <dt class="text-xs text-base-content/50">
            {t("inventory.invoices.tile_due_soon")}
          </dt>
          <dd class="text-lg font-semibold tabular-nums">
            {money(d.invoices.due_soon_total)}
          </dd>
        </RouterLink>
        <RouterLink
          to="/app/inventory/orders"
          class="rounded-lg bg-base-200 p-3 hover:bg-base-300/60"
        >
          <dt class="text-xs text-base-content/50">
            {t("inventory.orders.stat_open_value")}
          </dt>
          <dd class="text-lg font-semibold tabular-nums">
            {money(d.orders.awaiting_value)}
          </dd>
        </RouterLink>
      </dl>
    );

    const renderBelowPar = (d: Dashboard) =>
      d.stock.below_par.length === 0 ? (
        <p class="text-sm text-base-content/50">
          {t("inventory.overview.nothing_low")}
        </p>
      ) : (
        <ul class="space-y-2.5">
          {d.stock.below_par.map((i) => (
            <li key={i.id}>
              <div class="flex justify-between text-sm">
                <span class="truncate">{i.name}</span>
                <span class="whitespace-nowrap text-xs text-base-content/60">
                  {`${Number(i.on_hand.toFixed(1))} / ${Number(i.par_level.toFixed(1))} ${i.base_unit}`}
                </span>
              </div>
              <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-base-200">
                <div
                  class={[
                    "h-full rounded-full",
                    i.short_pct >= 75 ? "bg-error" : "bg-warning",
                  ].join(" ")}
                  style={{ width: `${Math.max(0, 100 - i.short_pct)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      );

    const renderPriceRises = (d: Dashboard) =>
      d.price_rises.length === 0 ? (
        <p class="text-sm text-base-content/50">
          {t("inventory.overview.no_price_rises")}
        </p>
      ) : (
        <table class="table table-sm">
          <tbody>
            {d.price_rises.map((p) => (
              <tr key={p.id}>
                <td class="px-0">
                  <div class="font-medium">{p.name}</div>
                  <div class="text-xs text-base-content/50">
                    {p.supplier_name}
                  </div>
                </td>
                <td class="whitespace-nowrap text-right text-xs text-base-content/60">
                  {`${p.previous_price.toFixed(2)} → ${p.price.toFixed(2)}${p.unit ? ` / ${p.unit}` : ""}`}
                </td>
                <td class="whitespace-nowrap px-0 text-right font-semibold text-error">{`+${p.change_pct.toFixed(1)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.overview")}
          subtitle={t("inventory.overview.subtitle")}
        >
          <div role="tablist" class="tabs tabs-boxed tabs-sm">
            {PERIODS.map((p) => (
              <button
                key={p}
                role="tab"
                class={["tab", days.value === p ? "tab-active" : ""].join(" ")}
                onClick={() => (days.value = p)}
              >
                {t("inventory.overview.period_days").replace(
                  "{days}",
                  String(p),
                )}
              </button>
            ))}
          </div>
        </SubpageHeader>

        <div class="flex-1 overflow-auto p-3">
          {!data.value ? (
            <div class="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div
              class={[
                "space-y-3 transition-opacity",
                loading.value ? "opacity-60" : "",
              ].join(" ")}
            >
              <div class="flex flex-wrap gap-2">
                {quick(
                  "document_scanner",
                  t("inventory.scan.scan_delivery"),
                  "/app/inventory/deliveries",
                )}
                {quick(
                  "add_shopping_cart",
                  t("inventory.orders.new"),
                  "/app/inventory/orders",
                )}
                {quick(
                  "checklist",
                  t("inventory.overview.start_count"),
                  "/app/inventory/counts",
                )}
                {quick(
                  "delete_sweep",
                  t("inventory.overview.log_waste"),
                  "/app/inventory/waste",
                )}
              </div>
              {renderKpis(data.value)}
              <div class="grid grid-cols-1 gap-3 xl:grid-cols-3">
                <div class="space-y-3 xl:col-span-2">
                  {card(
                    t("inventory.overview.attention"),
                    <AttentionList dashboard={data.value} />,
                  )}
                  {card(
                    t("inventory.overview.purchases_by_week"),
                    <WeeklyBars
                      weeks={data.value.purchases.weeks}
                      label={t("inventory.overview.purchases_by_week")}
                    />,
                  )}
                  {card(
                    t("inventory.overview.price_rises"),
                    renderPriceRises(data.value),
                  )}
                </div>
                <div class="space-y-3">
                  {card(t("inventory.overview.money"), renderMoney(data.value))}
                  {card(
                    t("inventory.overview.running_low"),
                    renderBelowPar(data.value),
                    data.value.stock.below_par_count > 0 && (
                      <button
                        class="btn btn-primary btn-xs"
                        onClick={() => (shopping.value = true)}
                      >
                        <span class="material-symbols-outlined text-sm">
                          shopping_cart
                        </span>
                        {t("inventory.overview.shopping_list")}
                      </button>
                    ),
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {shopping.value && (
          <ShoppingListModal onClose={() => (shopping.value = false)} />
        )}
      </div>
    );
  },
});
